"use client";

import { useCallback, useEffect, useState } from "react";
import { Any, METRICS_EVENTS } from "@cede/types";
import { API_ENDPOINT } from "@cede/utils";
import { clearPendingReferralCode, getReferralCodeFromSources } from "./referralCodeSources";
import { REFERRAL_QUERY_KEYS } from "./referralQueryKeys";
import { useReferrals } from "./useReferrals";
import { useApi } from "@/hooks/core/useApi";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useHubUser } from "@/hooks/user/useHubUser";
import { mixpanelClient } from "@/lib/mixpanel";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";

/**
 * Lightweight hook for auto-submitting referral codes.
 * Only fetches minimal data needed for auto-submit logic.
 * Used in AppShell - runs on every page.
 */
export const useReferralAutoSubmit = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { hubUser, authenticated, ready, auth } = useHubUser();
  const apiHelper = useApi();
  const queryClient = useQueryClient();
  const { trackEvent } = useMetricsEvents();

  // Check for pending code on client-side only (after hydration)
  const [hasPendingCode, setHasPendingCode] = useState(false);

  useEffect(() => {
    // Only check on client side after mount
    const code = getReferralCodeFromSources();
    setHasPendingCode(!!code);
  }, []);

  // Only fetch when there's a pending referral code
  const shouldFetch = !!hubUser?.id && authenticated && hasPendingCode;

  const { refereeReferral, isLoadingReferrals } = useReferrals({
    mode: "minimal",
    enabled: shouldFetch,
  });

  const { mutate: submitReferralCode } = useMutation({
    mutationFn: async (code: string) => {
      if (!code.trim()) return;

      try {
        trackEvent(METRICS_EVENTS.REFERRAL_CODE_SUBMITTED, { code });

        const result = await apiHelper.postAuth(auth, API_ENDPOINT.HUB_REFERRAL_ACCEPT, {
          bodyParams: { code: code.trim() },
        });

        if (result) {
          trackEvent(METRICS_EVENTS.REFERRAL_CODE_ACCEPTED, {
            referrer_user_id: result.referrerId,
            referee_user_id: result.refereeId,
            signup_date: new Date(result.createdAt || new Date()).toUTCString(),
          });

          // Set Mixpanel people properties for the new referral
          mixpanelClient.unionPeople({ referrerId: result.referrerId });
          mixpanelClient.setPeople({ referralStatus: result.status });
        }

        queryClient.invalidateQueries({ queryKey: [REFERRAL_QUERY_KEYS.REFERRALS] });
        enqueueSnackbar(`Successfully linked to referrer with code: ${code}`, { variant: "success" });
      } catch (error: Any) {
        const errorMessage = error?.message || "Failed to submit referral code";
        enqueueSnackbar(errorMessage, { variant: "error" });
        trackEvent(METRICS_EVENTS.REFERRAL_CODE_SUBMISSION_FAILED, { code, error: errorMessage });
      }
    },
  });

  const shouldAutoSubmit =
    authenticated && ready && hubUser?.defiAddress && !refereeReferral && !isLoadingReferrals && hasPendingCode;

  const handleAutoSubmit = useCallback(() => {
    const referralCode = getReferralCodeFromSources();
    if (shouldAutoSubmit && referralCode) {
      submitReferralCode(referralCode);
      clearPendingReferralCode();
    }
  }, [shouldAutoSubmit, submitReferralCode]);

  return {
    shouldAutoSubmit,
    handleAutoSubmit,
  };
};
